import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import {
    Col,
    Progress,
    Row,
    Spin,
    Typography,
    Card,
    Button,
    Statistic,
    Divider,
    Badge,
    Avatar,
    Modal,
    Input, 
    List, 
    Tag, 
    Space
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import QuestionItem from "./Fragments/QuestionItem";
import { io } from "socket.io-client";
import { 
    CheckCircleOutlined, 
    CloseCircleOutlined, 
    TrophyOutlined, 
    UserOutlined, 
    ArrowLeftOutlined, 
    ReloadOutlined,
    ThunderboltOutlined,
    SendOutlined,
    FireOutlined
} from '@ant-design/icons';
import { useTranslation } from "react-i18next";
import api from "../api";

const { Title, Text } = Typography;

const QuizBattle = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { battle: battleStats } = useSelector(state => state);
    
    const [socket, setSocket] = useState(null);
    const [searching, setSearching] = useState(true);
    const [battleData, setBattleId] = useState(null);
    const [quiz, setQuiz] = useState(null);
    const [loadingQuiz, setLoadingQuiz] = useState(false);
    const [onlineCount, setOnlineCount] = useState(0);
    
    const [step, setStep] = useState(0);
    const [points, setPoints] = useState(0);
    const [opponentProgress, setOpponentProgress] = useState({ score: 0, step: 0, finished: false });
    const [gameOver, setGameOver] = useState(false);
    const [result, setResult] = useState(null);
    const [myAnswers, setMyAnswers] = useState([]);
    const [showReview, setShowReview] = useState(false);
    
    // Chat states
    const [messages, setMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [showChat, setShowChat] = useState(window.innerWidth > 768);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    useEffect(() => {
        const newSocket = io(process.env.REACT_APP_CHAT_SERVER || 'https://chat-backend.avtodas.am');
        setSocket(newSocket);

        const name = localStorage.getItem('chat_user_name') || 'Անանուն';
        const age = localStorage.getItem('chat_user_age') || 0;

        newSocket.emit('join_battle', { name, age });

        newSocket.on('stats_update', (data) => {
            if (data && data.battleOnline !== undefined) {
                setOnlineCount(data.battleOnline);
            }
        });

        newSocket.on('battle_searching', () => {
            setSearching(true);
        });

        newSocket.on('battle_matched', (data) => {
            setBattleId(data);
            setSearching(false);
            fetchQuiz(data.quizId);
        });

        newSocket.on('opponent_progress', (data) => {
            setOpponentProgress(data);
        });

        newSocket.on('battle_message', (msg) => {
            setMessages(prev => [...prev, msg]);
        });

        newSocket.on('battle_over', (data) => {
            setGameOver(true);
            setResult(data);
            
            const myId = newSocket.id;
            if (data.winner === myId) {
                dispatch({ type: 'BATTLE_WIN', payload: { points: 100 } });
            } else if (data.winner === null) {
                dispatch({ type: 'BATTLE_DRAW', payload: { points: 30 } });
            } else {
                dispatch({ type: 'BATTLE_LOSS' });
            }
        });

        newSocket.on('opponent_left', () => {
            if (!gameOver) {
                Modal.warning({
                    title: t('battle.opponent_left_title', 'Մրցակիցը դուրս եկավ'),
                    content: t('battle.opponent_left_desc', 'Դուք հաղթեցիք տեխնիկական հաղթանակով:'),
                    onOk: () => navigate('/')
                });
                dispatch({ type: 'BATTLE_WIN', payload: { points: 50 } });
            }
        });

        return () => newSocket.disconnect();
    }, []);

    const fetchQuiz = (id) => {
        setLoadingQuiz(true);
        api.API.get(api.Quize + `/${id}`).then(data => {
            setQuiz(data);
            setLoadingQuiz(false);
        });
    };

    const handleSendChat = () => {
        if (!chatInput.trim()) return;
        socket.emit('send_battle_message', chatInput.trim());
        setChatInput('');
    };

    const handleNext = (variant) => {
        const currentQuestion = quiz.questions[step];
        const isCorrect = variant === currentQuestion.answer;
        
        let newPoints = points;
        if (isCorrect) newPoints++;
        
        setPoints(newPoints);
        const newStep = step + 1;
        setMyAnswers(prev => [...prev, variant]);
        setStep(newStep);

        socket.emit('update_battle_score', {
            score: newPoints,
            step: newStep,
            answer: variant,
            finished: newStep >= 20
        });
    };

    if (searching) {
        return (
            <div style={{ 
                minHeight: '80vh', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: 'radial-gradient(circle, #2d1b4e 0%, #1a1a2e 100%)',
                borderRadius: 20,
                margin: '20px 0',
                color: 'white',
                padding: '40px 20px'
            }}>
                <div className="searching-animation">
                    <ThunderboltOutlined style={{ fontSize: 80, color: '#722ed1', filter: 'drop-shadow(0 0 15px #722ed1)' }} />
                </div>
                <Title level={2} style={{ color: 'white', marginTop: 30, marginBottom: 10 }}>{t('battle.searching', 'Փնտրում ենք մրցակից...')}</Title>
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: '5px 15px', borderRadius: 20, marginBottom: 40 }}>
                    <Badge color="#52c41a" text={<span style={{ color: 'rgba(255,255,255,0.8)' }}>{onlineCount} {t('battle.players_online', 'խաղացող օնլայն')}</span>} />
                </div>
                
                <Row gutter={16} style={{ width: '100%', maxWidth: 400 }}>
                    <Col span={24}>
                        <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ textAlign: 'left' }}>
                                    <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{t('battle.your_wins', 'Ձեր հաղթանակները')}</Text>
                                    <Title level={4} style={{ color: 'white', margin: 0 }}>{battleStats.wins}</Title>
                                </div>
                                <TrophyOutlined style={{ fontSize: 32, color: '#faad14' }} />
                            </div>
                        </Card>
                    </Col>
                </Row>
                
                <Button 
                    size="large" 
                    danger
                    ghost
                    shape="round" 
                    onClick={() => navigate('/')} 
                    style={{ marginTop: 40, height: 50, padding: '0 40px', borderWidth: 2 }}
                >
                    {t('battle.cancel', 'Չեղարկել')}
                </Button>

                <style dangerouslySetInnerHTML={{ __html: `
                    @keyframes pulse {
                        0% { transform: scale(1); opacity: 0.8; }
                        50% { transform: scale(1.1); opacity: 1; }
                        100% { transform: scale(1); opacity: 0.8; }
                    }
                    .searching-animation {
                        animation: pulse 2s infinite ease-in-out;
                    }
                `}} />
            </div>
        );
    }

    if (loadingQuiz || !quiz) {
        return <div style={{ textAlign: 'center', padding: '100px' }}><Spin size="large" tip="Բեռնում ենք հարցաշարը..." /></div>;
    }

    return (
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: window.innerWidth < 768 ? '10px' : '20px' }}>
            <Row gutter={[24, 24]}>
                {/* Game Area */}
                <Col xs={24} lg={showChat && !showReview ? 16 : 24}>
                    <Row gutter={[16, 16]}>
                        {/* Scoreboard */}
                        <Col span={24}>
                            <Card 
                                style={{ 
                                    borderRadius: 20, 
                                    background: 'linear-gradient(90deg, #1a1a2e 0%, #16213e 100%)',
                                    border: 'none',
                                    overflow: 'hidden',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
                                }}
                                bodyStyle={{ padding: '20px' }}
                            >
                                <Row align="middle">
                                    <Col span={9}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                                            <Avatar 
                                                size={window.innerWidth < 768 ? 48 : 64} 
                                                src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${socket?.id}`} 
                                                style={{ border: '3px solid #1890ff' }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{t('battle.you', 'Դուք')}</Text>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                                                    <Title level={4} style={{ color: 'white', margin: 0 }}>{points}</Title>
                                                    <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>{step}/20</Text>
                                                </div>
                                                <Progress 
                                                    percent={(step / 20) * 100} 
                                                    showInfo={false} 
                                                    strokeColor="#1890ff" 
                                                    trailColor="rgba(255,255,255,0.1)"
                                                    size="small"
                                                    style={{ marginTop: 5 }}
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                    
                                    <Col span={6} style={{ textAlign: 'center' }}>
                                        <div className="vs-badge">VS</div>
                                        {!showReview && (
                                            <Button 
                                                type="link" 
                                                size="small" 
                                                onClick={() => setShowChat(!showChat)}
                                                style={{ color: 'rgba(255,255,255,0.5)', marginTop: 5 }}
                                            >
                                                {showChat ? t('battle.hide_chat', 'Թաքցնել') : t('battle.show_chat', 'Չատ')}
                                            </Button>
                                        )}
                                    </Col>

                                    <Col span={9}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 15, flexDirection: 'row-reverse' }}>
                                            <Avatar 
                                                size={window.innerWidth < 768 ? 48 : 64} 
                                                src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${battleData?.opponent.id}`} 
                                                style={{ border: '3px solid #ff4d4f' }}
                                            />
                                            <div style={{ flex: 1, textAlign: 'right' }}>
                                                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{battleData?.opponent.name?.substring(0, 10)}</Text>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexDirection: 'row-reverse' }}>
                                                    <Title level={4} style={{ color: 'white', margin: 0 }}>{opponentProgress.score}</Title>
                                                    <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>{opponentProgress.step}/20</Text>
                                                </div>
                                                <Progress 
                                                    percent={(opponentProgress.step / 20) * 100} 
                                                    showInfo={false} 
                                                    strokeColor="#ff4d4f" 
                                                    trailColor="rgba(255,255,255,0.1)"
                                                    size="small"
                                                    style={{ marginTop: 5 }}
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        {/* Question Area */}
                        <Col span={24}>
                            {!gameOver ? (
                                step < 20 ? (
                                    <div className="battle-question-card">
                                        <QuestionItem 
                                            currentQuestion={quiz.questions[step]} 
                                            handleNext={handleNext} 
                                            quick_view={true}
                                        />
                                    </div>
                                ) : (
                                    <Card style={{ textAlign: 'center', padding: '60px 40px', borderRadius: 20 }}>
                                        <Spin size="large" indicator={<FireOutlined spin style={{ fontSize: 48, color: '#722ed1' }} />} />
                                        <Title level={3} style={{ marginTop: 30 }}>{t('battle.waiting_opponent', 'Սպասում ենք մրցակցին...')}</Title>
                                        <Text type="secondary" style={{ fontSize: 16 }}>{t('battle.waiting_desc', 'Դուք ավարտել եք, տեսնենք ինչպես կպատասխանի մրցակիցը')}</Text>
                                    </Card>
                                )
                            ) : (
                                <>
                                    <Card 
                                        style={{ 
                                            textAlign: 'center', 
                                            borderRadius: 24, 
                                            overflow: 'hidden', 
                                            marginBottom: 20,
                                            border: 'none',
                                            boxShadow: '0 12px 48px rgba(0,0,0,0.12)'
                                        }}
                                    >
                                        <div style={{ 
                                            padding: '50px 20px', 
                                            background: result.winner === socket.id 
                                                ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)' 
                                                : result.winner === null 
                                                    ? 'linear-gradient(135deg, #fffbe6 0%, #fff1b8 100%)' 
                                                    : 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)'
                                        }}>
                                            {result.winner === socket.id ? (
                                                <>
                                                    <div className="victory-icon">
                                                        <TrophyOutlined style={{ fontSize: 80, color: '#52c41a', marginBottom: 20 }} />
                                                    </div>
                                                    <Title level={2} style={{ color: '#389e0d', marginBottom: 5 }}>{t('battle.victory', 'Հաղթանակ!')}</Title>
                                                    <Text strong style={{ color: '#52c41a', fontSize: 18 }}>+100 {t('battle.points_earned', 'միավոր')}</Text>
                                                </>
                                            ) : result.winner === null ? (
                                                <>
                                                    <div style={{ fontSize: 80, marginBottom: 20 }}>🤝</div>
                                                    <Title level={2} style={{ color: '#d48806', marginBottom: 5 }}>{t('battle.draw', 'Ոչ-ոքի')}</Title>
                                                    <Text strong style={{ color: '#d48806', fontSize: 18 }}>+30 {t('battle.points_earned', 'միավոր')}</Text>
                                                </>
                                            ) : (
                                                <>
                                                    <CloseCircleOutlined style={{ fontSize: 80, color: '#cf1322', marginBottom: 20 }} />
                                                    <Title level={2} style={{ color: '#cf1322', marginBottom: 5 }}>{t('battle.defeat', 'Պարտություն')}</Title>
                                                    <Text type="secondary" style={{ fontSize: 16 }}>{t('battle.try_again_desc', 'Մի հանձնվեք, փորձեք նորից!')}</Text>
                                                </>
                                            )}
                                        </div>
                                        
                                        <div style={{ padding: '30px' }}>
                                            <Row gutter={16} justify="center">
                                                <Col xs={12} sm={8}>
                                                    <Card size="small" style={{ background: '#f0f2f5', borderRadius: 16, border: 'none' }}>
                                                        <Statistic title={t('battle.you', 'Դուք')} value={points} suffix="/ 20" valueStyle={{ fontSize: 24, fontWeight: 'bold' }} />
                                                    </Card>
                                                </Col>
                                                <Col xs={12} sm={8}>
                                                    <Card size="small" style={{ background: '#f0f2f5', borderRadius: 16, border: 'none' }}>
                                                        <Statistic title={battleData?.opponent.name} value={opponentProgress.score} suffix="/ 20" valueStyle={{ fontSize: 24, fontWeight: 'bold' }} />
                                                    </Card>
                                                </Col>
                                            </Row>

                                            <div style={{ marginTop: 40 }}>
                                                <Space size="large" wrap justify="center">
                                                    <Button 
                                                        type="primary" 
                                                        size="large" 
                                                        onClick={() => window.location.reload()} 
                                                        icon={<ReloadOutlined />}
                                                        style={{ height: 50, padding: '0 40px', borderRadius: 12, fontWeight: 'bold' }}
                                                    >
                                                        {t('battle.play_again', 'Նորից')}
                                                    </Button>
                                                    <Button 
                                                        size="large"
                                                        onClick={() => setShowReview(!showReview)} 
                                                        icon={<CheckCircleOutlined />} 
                                                        danger={showReview}
                                                        style={{ height: 50, borderRadius: 12 }}
                                                    >
                                                        {showReview ? t('battle.hide_review', 'Փակել') : t('questions.review_answers')}
                                                    </Button>
                                                    <Button 
                                                        size="large"
                                                        onClick={() => navigate('/')} 
                                                        icon={<ArrowLeftOutlined />}
                                                        style={{ height: 50, borderRadius: 12 }}
                                                    >
                                                        {t('battle.back_home', 'Տուն')}
                                                    </Button>
                                                </Space>
                                            </div>
                                        </div>
                                    </Card>

                                    {showReview && result && (
                                        <div className="battle-review-container">
                                            <Divider><Title level={4}>{t('questions.review_answers')}</Title></Divider>
                                            {quiz.questions.map((q, idx) => {
                                                const myAns = myAnswers[idx];
                                                const oppAnswers = socket.id === result.u1Id ? result.u2Answers : result.u1Answers;
                                                const oppAns = oppAnswers ? oppAnswers[idx] : undefined;
                                                
                                                return (
                                                    <Card key={idx} size="small" style={{ marginBottom: 16, borderRadius: 16, border: '1px solid #f0f0f0' }} bodyStyle={{ padding: '16px' }}>
                                                        <Row gutter={[16, 12]}>
                                                            <Col span={24}>
                                                                <Text strong style={{ fontSize: 15 }}>{idx + 1}. {q.question}</Text>
                                                                {q.image && <div style={{ marginTop: 12 }}><img src={process.env.REACT_APP_API_ENDPOINT + q.image} style={{ width: '100%', maxWidth: 300, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} /></div>}
                                                            </Col>
                                                            <Col xs={24} sm={12}>
                                                                <div style={{ 
                                                                    padding: '12px', 
                                                                    borderRadius: 12, 
                                                                    background: myAns === q.answer ? '#f6ffed' : '#fff1f0', 
                                                                    border: `1px solid ${myAns === q.answer ? '#b7eb8f' : '#ffa39e'}` 
                                                                }}>
                                                                    <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}>{t('battle.you', 'Դուք')}</div>
                                                                    <Text style={{ fontSize: 13, fontWeight: 500 }}>{q.variants[myAns]?.value || 'N/A'}</Text>
                                                                </div>
                                                            </Col>
                                                            <Col xs={24} sm={12}>
                                                                <div style={{ 
                                                                    padding: '12px', 
                                                                    borderRadius: 12, 
                                                                    background: oppAns === q.answer ? '#f6ffed' : '#fff1f0', 
                                                                    border: `1px solid ${oppAns === q.answer ? '#b7eb8f' : '#ffa39e'}` 
                                                                }}>
                                                                    <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}>{battleData?.opponent.name}</div>
                                                                    <Text style={{ fontSize: 13, fontWeight: 500 }}>{q.variants[oppAns]?.value || 'N/A'}</Text>
                                                                </div>
                                                            </Col>
                                                            {(myAns !== q.answer || oppAns !== q.answer) && (
                                                                <Col span={24}>
                                                                    <div style={{ background: '#e6f7ff', padding: '10px 15px', borderRadius: 10, borderLeft: '4px solid #1890ff' }}>
                                                                        <Text style={{ fontSize: 13 }}><span style={{ color: '#1890ff', fontWeight: 'bold' }}>{t('questions.correct_answer', 'Ճիշտ պատասխան')}:</span> {q.variants[q.answer].value}</Text>
                                                                    </div>
                                                                </Col>
                                                            )}
                                                        </Row>
                                                    </Card>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}
                        </Col>
                    </Row>
                </Col>

                {/* Chat Area */}
                {showChat && !showReview && (
                    <Col xs={24} lg={8}>
                        <Card 
                            size="small"
                            title={<Space><SendOutlined style={{ color: '#1890ff' }} /> <Text strong>{t('battle.chat_title', 'Չատ')}</Text></Space>}
                            bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: window.innerWidth < 768 ? '350px' : '550px' }}
                            style={{ borderRadius: 20, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', border: 'none' }}
                        >
                            <div style={{ flex: 1, overflowY: 'auto', padding: '15px', background: '#f0f2f5' }}>
                                <List
                                    dataSource={messages}
                                    renderItem={item => {
                                        const isMe = item.from === socket?.id;
                                        return (
                                            <div style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
                                                <div style={{ 
                                                    maxWidth: '85%', 
                                                    padding: '10px 14px', 
                                                    borderRadius: isMe ? '18px 18px 0 18px' : '18px 18px 18px 0', 
                                                    background: isMe ? '#1890ff' : '#fff',
                                                    color: isMe ? '#fff' : '#333',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                    fontSize: 13
                                                }}>
                                                    {item.text}
                                                </div>
                                            </div>
                                        );
                                    }}
                                />
                                <div ref={messagesEndRef} />
                            </div>
                            <div style={{ padding: '15px', borderTop: '1px solid #f0f0f0', background: '#fff' }}>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <Input 
                                        placeholder={t('battle.type_message', 'Գրեք հաղորդագրություն...')}
                                        value={chatInput}
                                        onChange={e => setChatInput(e.target.value)}
                                        onPressEnter={handleSendChat}
                                        style={{ borderRadius: 10 }}
                                    />
                                    <Button 
                                        type="primary" 
                                        icon={<SendOutlined />} 
                                        onClick={handleSendChat}
                                        style={{ borderRadius: 10 }}
                                    />
                                </div>
                            </div>
                        </Card>
                    </Col>
                )}
            </Row>
            
            <style dangerouslySetInnerHTML={{ __html: `
                .vs-badge {
                    display: inline-block;
                    padding: 4px 12px;
                    background: #ff4d4f;
                    color: white;
                    font-weight: 900;
                    border-radius: 8px;
                    font-size: 18px;
                    letter-spacing: 1px;
                    box-shadow: 0 0 15px rgba(255, 77, 79, 0.4);
                    margin-bottom: 5px;
                }
                .battle-question-card .ant-card {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.06);
                }
                .victory-icon {
                    animation: bounce 1s infinite alternate;
                }
                @keyframes bounce {
                    from { transform: translateY(0); }
                    to { transform: translateY(-10px); }
                }
                @media (max-width: 767px) {
                    .vs-badge { font-size: 14px; padding: 2px 8px; }
                    .ant-statistic-title { font-size: 12px; }
                    .ant-statistic-content { font-size: 18px !important; }
                }
            `}} />
        </div>
    );
};

export default QuizBattle;
